import { ReactElement, ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { colors } from '~/styles/colors';

const Frame = styled.button<{ $ratio: string; $maxWidth?: number; $grow?: boolean }>`
  display: block;
  width: 100%;
  min-width: 0;
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : 'none')};
  aspect-ratio: ${({ $ratio }) => $ratio};
  padding: 0;
  overflow: hidden;
  border: 1px solid ${colors.stroke['soft-200']};
  border-radius: 8px;
  background: ${colors.bg['white-0']};
  cursor: zoom-in;
  ${({ $grow }) => $grow && css`
    flex: 1;
    border-radius: 10px;
  `}

  & > svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const ZoomOverlay = styled.dialog`
  position: fixed;
  inset: 0;
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  z-index: 1000001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.celsius['overlay-strong']};
  cursor: zoom-out;
`;

const ZoomFrame = styled.div<{ $ratio: string }>`
  width: min(94vw, 1400px);
  max-height: 92vh;
  aspect-ratio: ${({ $ratio }) => $ratio};
  overflow: hidden;
  border: 1px solid ${colors.stroke['soft-200']};
  border-radius: 12px;
  background: ${colors.bg['white-0']};
  box-shadow: 0 24px 64px ${colors.celsius['shadow-modal']};

  & > svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

interface ZoomableIllustrationProps {
  /** Proporção do desenho, como no CSS `aspect-ratio` (ex.: "1280 / 700"). */
  ratio: string;
  label: string;
  layout?: { maxWidth?: number; grow?: boolean };
  children: ReactNode;
}

/** Ilustração do design que amplia ao clicar e fecha ao clicar de novo. */
export function ZoomableIllustration({
  ratio, label, layout, children,
}: Readonly<ZoomableIllustrationProps>): ReactElement {
  const { t } = useTranslation();
  const [isZoomed, setIsZoomed] = useState(false);

  return (
    <>
      <Frame
        type="button"
        $ratio={ratio}
        $maxWidth={layout?.maxWidth}
        $grow={layout?.grow}
        title={t('ecoSimplificadoCliqueParaAmpliar')}
        aria-label={t('ecoSimplificadoAmpliarIlustracao', { nome: label, interpolation: { escapeValue: false } })}
        onClick={() => setIsZoomed(true)}
      >
        {children}
      </Frame>
      {isZoomed ? createPortal(
        <ZoomOverlay open onClick={() => setIsZoomed(false)} aria-label={label}>
          <ZoomFrame $ratio={ratio}>{children}</ZoomFrame>
        </ZoomOverlay>,
        document.body,
      ) : null}
    </>
  );
}
